import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type UiLanguage = "ja" | "en";

type UiLanguageContextValue = {
  language: UiLanguage;
  isEnglish: boolean;
  setLanguage: (language: UiLanguage) => void;
  pick: (ja: string, en: string) => string;
};

const STORAGE_KEY = "webpress.ui-language";

const UiLanguageContext = createContext<UiLanguageContextValue | null>(null);

function readInitialLanguage(): UiLanguage {
  if (typeof window === "undefined") return "ja";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === "en" ? "en" : "ja";
}

export function UiLanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<UiLanguage>(readInitialLanguage);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const pick = useCallback(
    (ja: string, en: string) => (language === "en" ? en : ja),
    [language],
  );

  const value = useMemo<UiLanguageContextValue>(
    () => ({
      language,
      isEnglish: language === "en",
      setLanguage,
      pick,
    }),
    [language, pick],
  );

  return <UiLanguageContext.Provider value={value}>{children}</UiLanguageContext.Provider>;
}

export function useUiLanguage() {
  const context = useContext(UiLanguageContext);
  if (!context) {
    throw new Error("useUiLanguage must be used inside UiLanguageProvider");
  }
  return context;
}
